import { jsPDF } from "jspdf";

import { Permiso } from "../shared/model/permisos/permiso.model";
import { LOGO_SOLSER } from "../shared/frc/logo-solser";

/**
 * El FRC-017 Permiso, dibujado fiel al formato controlado (V 1.1,
 * liberado el 21/06/2018): portada con el logo, y la hoja con el
 * encabezado del área, las tres opciones, el goce y las firmas.
 *
 * Sólo cambia lo que en el formato original está marcado en rojo: los
 * datos del permiso y las firmas trazadas en pantalla. La línea de RRHH
 * queda impresa como en el original, porque esa no se firma en el COS.
 *
 * LAS TRES OPCIONES SON EXCLUYENTES. En el papel hay tres casillas y
 * sólo se marca una; aquí se dibuja la "X" en la que corresponda al
 * tipo y se deja el dato al lado. Las otras dos van vacías, igual que
 * si se imprimiera en blanco.
 *
 * Las medidas salen de medir el documento original (carta, 612 x 792
 * pt). No se inventó el diseño: se calcó, como el FRC-019.
 */

const AZUL: [number, number, number] = [55, 73, 87];
const TINTA: [number, number, number] = [33, 41, 48];

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function parte(iso: string): { d: number; mes: string; anio: number } {
  const p = iso.substring(0, 10).split("-");
  return {
    d: Number(p[2]),
    mes: MESES[Number(p[1]) - 1] || "",
    anio: Number(p[0]),
  };
}

/** "12 de noviembre de 2026" */
function larga(iso?: string): string {
  if (!iso) {
    return "";
  }
  const f = parte(iso);
  return f.d + " de " + f.mes + " de " + f.anio;
}

/**
 * "14:30 hrs del 12 de noviembre de 2026".
 *
 * En salida y entrada tarde el papel llevaba sólo la hora, y el día
 * quedaba fuera del documento: la fecha de arriba es la de la ciudad —el
 * día en que se pidió— así que un permiso pedido el 1 para salir el 15
 * se imprimía sin que el 15 apareciera en ninguna parte. Quien firma
 * tiene que saber qué día está autorizando.
 */
function horaConDia(hora?: string, iso?: string): string {
  const h = (hora || "").trim();
  const dia = larga(iso);
  if (!h) {
    return dia;
  }
  return dia ? h + " hrs del " + dia : h + " hrs";
}

/**
 * Encaja un texto en un ancho bajando la fuente; nunca lo parte. Un
 * nombre largo se ve más chico, pero no pisa la etiqueta ni se sale de
 * su línea.
 */
function encaja(pdf: jsPDF, texto: string, ancho: number, base: number): void {
  let s = base;
  pdf.setFontSize(s);
  while (s > 7 && pdf.getTextWidth(texto) > ancho) {
    s -= 0.5;
    pdf.setFontSize(s);
  }
}

/** Sólo el trazo se imprime. "FISICA" deja la línea en blanco para firmar a mano. */
const esTrazo = (f?: string) => !!f && f.startsWith("data:image/");

export function generaFrc017(p: Permiso): void {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });

  /* ============ Portada ============ */
  pdf.addImage(LOGO_SOLSER, "JPEG", 114, 154, 383, 132);
  pdf.setFont("times", "bold");
  pdf.setFontSize(26);
  pdf.setTextColor(AZUL[0], AZUL[1], AZUL[2]);
  pdf.text("Permiso", 540, 437, { align: "right" });

  pdf.setDrawColor(AZUL[0], AZUL[1], AZUL[2]);
  pdf.setLineWidth(2);
  pdf.line(29, 698, 556, 698);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.text("© Copyright Sol Ser Sistem S.A. de C.V.", 306, 716, { align: "center" });
  pdf.setLineWidth(2.5);
  pdf.line(0, 780, 612, 780);

  /* ============ La hoja del formato ============ */
  pdf.addPage();
  pdf.addImage(LOGO_SOLSER, "JPEG", 62, 14, 165, 57);

  pdf.setTextColor(AZUL[0], AZUL[1], AZUL[2]);
  pdf.setFontSize(10.5);
  pdf.text("FRC-017 Permiso <V 1.1>", 545, 30, { align: "right" });
  pdf.text("Fecha Liberación del Formato <21/06/2018>", 545, 44, { align: "right" });
  pdf.text("RYC", 545, 59, { align: "right" });
  pdf.setLineWidth(2.5);
  pdf.line(63, 77, 545, 77);

  /* ---- La fecha de la ciudad: el día en que se pidió ---- */
  const creado = (p.fechaCreacion || "").substring(0, 10) || p.fecha || "";
  const f = parte(creado);
  pdf.setTextColor(TINTA[0], TINTA[1], TINTA[2]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text(
    "Querétaro, Qro,  a " + f.d + "  de " + f.mes + " de " + f.anio,
    545, 138, { align: "right" }
  );

  /* ---- El encabezado: las cuatro líneas del área ---- */
  const cabecera: Array<[string, string]> = [
    ["Área :", p.area || ""],
    ["Director de Área :", p.directorArea || ""],
    ["Gerente Operativo :", p.gerenteOperativo || ""],
    ["Líder de Proyecto/ Jefe directo :", p.jefeDirecto || ""],
  ];
  let y = 190;
  for (const [etiqueta, valor] of cabecera) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.text(etiqueta, 300, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    encaja(pdf, valor, 230, 10.5);
    pdf.text(valor, 310, y - 1);
    pdf.setFontSize(10.5);
    pdf.setLineWidth(0.75);
    pdf.setDrawColor(TINTA[0], TINTA[1], TINTA[2]);
    pdf.line(305, y + 3, 545, y + 3);
    y += 19;
  }

  /* ---- "se le notifica que al trabajador ____" ---- */
  y += 12;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10.5);
  const frase = "Por este conducto se le notifica que al trabajador";
  pdf.text(frase, 71, y);
  const finFrase = 71 + pdf.getTextWidth(frase) + 8;
  const nombre = p.colaborador || "";
  encaja(pdf, nombre, 545 - finFrase - 4, 10.5);
  pdf.text(nombre, finFrase, y - 1);
  pdf.setFontSize(10.5);
  pdf.line(finFrase - 4, y + 3, 545, y + 3);

  y += 20;
  pdf.text("le ha sido concedido permiso para:", 71, y);

  /* ---- Las tres opciones. Sólo se marca UNA ---- */
  y += 26;
  const opciones: Array<[string, string, string]> = [
    // [tipo, texto del formato, el dato que va en la línea]
    ["FALTA", "Faltar a sus labores el día", larga(p.fecha)],
    ["SALIDA", "Salir dentro de la jornada de trabajo, hora de salida",
      horaConDia(p.hora, p.fecha)],
    ["ENTRADA", "Llegar más tarde, hora de entrada",
      horaConDia(p.hora, p.fecha)],
  ];
  for (const [tipo, texto, dato] of opciones) {
    const marcada = p.tipo === tipo;

    // La viñeta y el paréntesis, como en el papel.
    pdf.setFillColor(TINTA[0], TINTA[1], TINTA[2]);
    pdf.circle(80, y - 3, 1.6, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    pdf.text("(   )", 92, y);

    // La X sólo en la que se escogió.
    if (marcada) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("X", 96.3, y);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.5);
    }

    pdf.text(texto, 118, y);
    const finTexto = 118 + pdf.getTextWidth(texto) + 8;

    // El dato y su línea, sólo en la opción marcada: en las otras dos el
    // papel va en blanco y aquí también.
    pdf.setLineWidth(0.75);
    pdf.line(finTexto, y + 3, 545, y + 3);
    if (marcada && dato) {
      pdf.setFont("helvetica", "bold");
      encaja(pdf, dato, 545 - finTexto - 6, 10.5);
      pdf.text(dato, finTexto + 3, y - 1);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.5);
    }
    y += 20;
  }

  /* ---- Observaciones: tres renglones, como el formato ---- */
  y += 14;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  pdf.text("OBSERVACIONES", 71, y);
  pdf.setFont("helvetica", "normal");

  const obs = pdf.splitTextToSize(p.observaciones || "", 370);
  const inicioObs = 71 + pdf.getTextWidth("OBSERVACIONES") + 12;
  // El primer renglón arranca a la derecha del rótulo; los otros dos, al
  // margen. Es como está impreso el original.
  if (obs.length > 0) {
    pdf.text(String(obs[0]), inicioObs, y - 1);
  }
  pdf.setLineWidth(0.75);
  pdf.line(inicioObs - 4, y + 3, 545, y + 3);
  for (let i = 1; i <= 3; i++) {
    const ly = y + i * 15;
    pdf.line(71, ly + 3, 545, ly + 3);
    if (obs.length > i) {
      pdf.text(String(obs[i]), 74, ly - 1);
    }
  }

  /* ---- El goce: dos casillas, una marcada ---- */
  y += 3 * 15 + 26;
  const goces: Array<[string, string]> = [
    ["CON", "Con goce de sueldo"],
    ["SIN", "Sin goce de sueldo"],
  ];
  for (const [valor, texto] of goces) {
    pdf.setFillColor(TINTA[0], TINTA[1], TINTA[2]);
    pdf.circle(80, y - 3, 1.6, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    pdf.text("(   )", 92, y);
    if (p.goce === valor) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("X", 96.3, y);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.5);
    }
    pdf.text(texto, 118, y);
    y += 18;
  }

  /* ---- Firmas ---- */
  y += 20;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Firman de conformidad:", 71, y);

  const yRotulo = y + 30;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10.5);
  pdf.text("Recurso", 175, yRotulo, { align: "center" });
  pdf.text("SOLSER", 458, yRotulo, { align: "center" });

  // El trazo se apoya en la línea, entre el rótulo y ella.
  const yLinea = yRotulo + 42;
  if (esTrazo(p.firmaSolicita)) {
    pdf.addImage(p.firmaSolicita!, "PNG", 125, yRotulo + 2, 100, 38);
  }
  if (esTrazo(p.firmaAutoriza)) {
    pdf.addImage(p.firmaAutoriza!, "PNG", 408, yRotulo + 2, 100, 38);
  }

  pdf.setLineWidth(1);
  pdf.line(99, yLinea, 252, yLinea);
  pdf.line(376, yLinea, 540, yLinea);

  // Bajo la línea va QUIÉN firmó, no el rótulo "Nombre Recurso". En el
  // formato en blanco esa leyenda dice qué escribir ahí; con el nombre
  // ya puesto sobra, y dejarla obligaba a leer dos renglones para
  // enterarse de uno.
  pdf.setFontSize(10.5);
  encaja(pdf, p.colaborador || "", 150, 10.5);
  pdf.text(p.colaborador || "", 175, yLinea + 15, { align: "center" });

  // Bajo SOLSER, sólo el nombre de quien firma y con la misma letra que
  // el del recurso (13/10/2026), igual que en el FRC-019. Con el puesto
  // detrás («– Dirección Desarrollo») el renglón no cabía bajo su línea y
  // encaja() lo imprimía más chico.
  const quienAutoriza = (p.autorizaNombre || "").trim();
  encaja(pdf, quienAutoriza, 165, 10.5);
  pdf.text(quienAutoriza, 458, yLinea + 15, { align: "center" });
  pdf.setFontSize(10.5);

  /* ---- RRHH: impresa, como en el original ---- */
  const yRh = yLinea + 58;
  pdf.text("SOLSER", 306, yRh, { align: "center" });
  pdf.line(240, yRh + 44, 372, yRh + 44);
  pdf.text("Dirección de RRHH", 306, yRh + 58, { align: "center" });

  pdf.setFontSize(7.5);
  pdf.setTextColor(120, 130, 140);
  pdf.text("Confidencial", 72, 752);
  pdf.setDrawColor(AZUL[0], AZUL[1], AZUL[2]);
  pdf.setLineWidth(2.5);
  pdf.line(0, 780, 612, 780);

  const quien = (p.colaborador || "colaborador").replace(/\s+/g, " ").trim();
  pdf.save("FRC-017_Permiso " + quien + " " + (p.fecha || "") + ".pdf");
}
